import { formatFileSize, resolveAttachmentKind } from '../../utils/fileutils';

/** 与默认校验一致的 HTML `accept` 字符串（图片 + PDF + Word） */
export const DEFAULT_FILE_ATTACHMENT_ACCEPT =
  'image/*,.pdf,.PDF';

/** 默认单文件大小上限（字节）；0 表示不限制 */
export const DEFAULT_FILE_ATTACHMENT_MAX_BYTES = 20 * 1024 * 1024;

export const DEFAULT_FILE_ATTACHMENT_INPUT_ID = 'feedback-attach-file-input';

export function sanitizeAttachmentInputId(id) {
  return String(id || DEFAULT_FILE_ATTACHMENT_INPUT_ID).replace(/[^a-zA-Z0-9_-]/g, '') || DEFAULT_FILE_ATTACHMENT_INPUT_ID;
}

function escapeHtmlAttr(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');
}

/**
 * 按 HTML accept 常见写法校验（image/*、后缀、完整 MIME）
 * @param {File} file
 * @param {string} acceptAttr
 */
export function fileMatchesAcceptList(file, acceptAttr) {
  if (!acceptAttr || !String(acceptAttr).trim()) return true;
  const tokens = String(acceptAttr)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  if (!tokens.length) return true;

  const mime = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();

  for (const raw of tokens) {
    const t = raw.toLowerCase();
    if (t === 'image/*' && mime.startsWith('image/')) return true;
    if (t.endsWith('/*')) {
      const prefix = t.slice(0, -1);
      if (mime.startsWith(prefix)) return true;
    }
    if (t.startsWith('.')) {
      if (name.endsWith(t)) return true;
    }
    if (t.includes('/') && !t.endsWith('/*') && mime === t) return true;
  }
  return false;
}

/**
 * @param {File} file
 * @param {{ accept?: string, maxFileBytes?: number }} options maxFileBytes 0 或未设置表示不限制
 * @returns {{ ok: true } | { ok: false, reason: 'empty' | 'size' | 'type' }}
 */
export function validateAttachmentFile(file, options = {}) {
  const { accept = '', maxFileBytes = 0 } = options;
  if (!file) return { ok: false, reason: 'empty' };
  const limit = Number(maxFileBytes) || 0;
  if (limit > 0 && file.size > limit) {
    return { ok: false, reason: 'size', maxFileBytes: limit };
  }
  if (accept && !fileMatchesAcceptList(file, accept)) {
    return { ok: false, reason: 'type' };
  }
  return { ok: true };
}

export function truncateFileName(name, maxLen = 26) {
  if (!name || name.length <= maxLen) return name;
  const extMatch = name.match(/(\.[^.]+)$/);
  const ext = extMatch ? extMatch[1] : '';
  const stem = ext ? name.slice(0, -ext.length) : name;
  const budget = maxLen - ext.length - 3;
  if (budget < 4) return `${name.slice(0, maxLen - 3)}...`;
  return `${stem.slice(0, budget)}...${ext}`;
}



/**
 * 更新输入区内附件条展示
 * @param {HTMLElement | null} container
 * @param {File | null} file
 * @param {{ maxNameLen?: number }} [opts]
 */
export function updateAttachmentChipView(container, file, opts = {}) {
  if (!container) return;
  const { maxNameLen = 26 } = opts;
  const chip = container.querySelector('.file-attachment-chip');
  const compose = container.querySelector('.textarea-compose');
  if (compose) {
    compose.classList.toggle('has-attachment', !!file);
  }
  if (!chip) return;

  if (!file) {
    chip.hidden = true;
    chip.style.display = 'none';
    return;
  }

  chip.hidden = false;
  chip.style.display = 'flex';

  const nameEl = chip.querySelector('.file-attachment-chip__name');
  const sizeEl = chip.querySelector('.file-attachment-chip__size');
  const iconEl = chip.querySelector('.file-attachment-chip__icon');

  if (nameEl) nameEl.textContent = truncateFileName(file.name, maxNameLen);
  if (sizeEl) sizeEl.textContent = formatFileSize(file.size);

  if (iconEl) {
    iconEl.classList.remove('is-pdf', 'is-image');
    const kind = resolveAttachmentKind(file);
    if (kind === 'pdf') {
      iconEl.textContent = 'PDF';
      iconEl.classList.add('is-pdf');
    } else if (kind === 'image') {
      iconEl.textContent = 'IMG';
      iconEl.classList.add('is-image');
    }
  }
}

export function clearAttachmentFileInput(container, inputId = DEFAULT_FILE_ATTACHMENT_INPUT_ID) {
  const idSafe = sanitizeAttachmentInputId(inputId);
  const el = container?.querySelector(`#${idSafe}`);
  if (el) el.value = '';
}

/**
 * @param {object} p
 * @param {string} p.inputId
 * @param {string} p.accept
 * @param {string} p.pickButtonTitle
 * @param {string} p.removeButtonTitle
 */
export function renderFileAttachmentPickerParts({
  inputId = DEFAULT_FILE_ATTACHMENT_INPUT_ID,
  accept = DEFAULT_FILE_ATTACHMENT_ACCEPT,
  pickButtonTitle = '',
  removeButtonTitle = '',
}) {
  const idSafe = sanitizeAttachmentInputId(inputId);
  const safeAccept = escapeHtmlAttr(accept);
  const safePick = escapeHtmlAttr(pickButtonTitle);
  const safeRemove = escapeHtmlAttr(removeButtonTitle);

  const hiddenInputHtml = `<input type="file" id="${idSafe}" class="feedback-attach-file-input" accept="${safeAccept}" tabindex="-1" aria-hidden="true" />`;

  const chipHtml = `
                            <div class="file-attachment-chip" hidden>
                                <div class="file-attachment-chip__icon" aria-hidden="true">PDF</div>
                                <div class="file-attachment-chip__meta">
                                    <span class="file-attachment-chip__name"></span>
                                    <span class="file-attachment-chip__size"></span>
                                </div>
                                <button type="button" class="file-attachment-chip__remove" title="${safeRemove}" aria-label="${safeRemove}">&times;</button>
                            </div>`;

  const triggerButtonHtml = `
                <button type="button" class="input-attach-file-btn" title="${safePick}">+</button>`;

  return { hiddenInputHtml, chipHtml, triggerButtonHtml };
}

/**
 * @param {object} opts
 * @param {HTMLElement} opts.container
 * @param {string} [opts.inputId]
 * @param {string} [opts.accept]
 * @param {number} [opts.maxFileBytes] 0 不限制
 * @param {() => boolean} [opts.canOpen] 返回 false 则拦截打开（如语音模式）
 * @param {() => void} [opts.onOpenDenied]
 * @param {() => boolean} [opts.hasExistingFile]
 * @param {() => void} [opts.onExistingFileDenied]
 * @param {(file: File) => void} opts.onFileAccepted
 * @param {(result: { reason: 'size' | 'type', maxFileBytes?: number }) => void} [opts.onFileRejected]
 * @param {() => void} [opts.onRemoved]
 */
export function bindFileAttachmentPicker(opts) {
  const {
    container,
    inputId = DEFAULT_FILE_ATTACHMENT_INPUT_ID,
    accept = DEFAULT_FILE_ATTACHMENT_ACCEPT,
    maxFileBytes = DEFAULT_FILE_ATTACHMENT_MAX_BYTES,
    canOpen = () => true,
    onOpenDenied = () => {},
    hasExistingFile = () => false,
    onExistingFileDenied = () => {},
    onFileAccepted,
    onFileRejected = () => {},
    onRemoved = () => {},
  } = opts;

  if (!container || typeof onFileAccepted !== 'function') return;

  const inputIdSafe = sanitizeAttachmentInputId(inputId);
  const input = container.querySelector(`#${inputIdSafe}`);
  const onChange = (e) => {
    const el = e.target;
    const file = el.files?.[0];
    if (!file) return;
    if (hasExistingFile()) {
      onExistingFileDenied();
      el.value = '';
      return;
    }
    const v = validateAttachmentFile(file, { accept, maxFileBytes });
    if (!v.ok) {
      onFileRejected(v);
      el.value = '';
      return;
    }
    onFileAccepted(file);
    el.value = '';
  };

  const onPickClick = (e) => {
    e?.preventDefault();
    if (!canOpen()) {
      onOpenDenied();
      return;
    }
    if (hasExistingFile()) {
      onExistingFileDenied();
      return;
    }
    input?.click();
  };

  const onRemoveClick = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    clearAttachmentFileInput(container, inputId);
    updateAttachmentChipView(container, null);
    onRemoved();
  };

  if (input) {
    input.addEventListener('change', onChange);
  }
  container.querySelectorAll('.input-attach-file-btn').forEach((btn) => {
    btn.addEventListener('click', onPickClick);
  });
  const chipRemove = container.querySelector('.file-attachment-chip__remove');
  if (chipRemove) {
    chipRemove.addEventListener('click', onRemoveClick);
  }
}
